import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../../utils/api'
import { toast } from 'react-toastify';
import { useAccountContext } from '../AdminAccountPage';
import { AuditEvent, AuditEventFilters } from '../types';
import { useUserTimezone } from '../../../../hooks/useUserTimezone';

// ========================================
// HTML Response Container - For legacy HTML rendering
// ========================================

const HtmlContainer = styled.div`
  width: 100%;
  min-height: 400px;
  
  /* Style injected HTML to match admin2 styling */
  & table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 4px;
  }
  
  & th, & td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34,36,38,.1);
  }
  
  & th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0,0,0,.87);
    border-top: none;
  }
  
  & tr:hover td {
    background: #f9fafb;
  }
  
  & a {
    color: #4183c4;
    text-decoration: none;
  }
  
  & a:hover {
    text-decoration: underline;
  }
  
  & .ui.form {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    align-items: flex-end;
  }
  
  & .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  & .ui.button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  
  & .ui.primary.button {
    background: #2185d0;
    color: #fff;
  }
  
  & .pagination {
    display: flex;
    gap: 4px;
    margin-top: 16px;
  }
`;

// ========================================
// Styled Components - Matching Semantic UI
// ========================================

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 24px 0;
`;

const FormContainer = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  align-items: flex-end;
`;

const FieldGroup = styled.div<{ $width?: string; $hidden?: boolean }>`
  display: ${props => props.$hidden ? 'none' : 'flex'};
  flex-direction: column;
  gap: 4px;
  width: ${props => props.$width || 'auto'};
`;

const FieldLabel = styled.label`
  font-size: 13px;
  color: #333;
  font-weight: 600;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid rgba(34,36,38,.15);
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  min-width: 160px;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const DateTimeInput = styled.input`
  padding: 9px 12px;
  border: 1px solid rgba(34,36,38,.15);
  border-radius: 4px;
  font-size: 14px;
  min-width: 180px;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const TextInput = styled.input`
  padding: 9px 12px;
  border: 1px solid rgba(34,36,38,.15);
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const PrimaryButton = styled.button`
  padding: 10px 20px;
  background: #2185d0;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #1678c2;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 20px;
  background: #e0e1e2;
  color: rgba(0,0,0,.6);
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #cacbcd;
    color: rgba(0,0,0,.8);
  }
`;

const DownloadRow = styled.div`
  margin-bottom: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid rgba(34,36,38,.15);
  border-radius: 4px;
  
  th, td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34,36,38,.1);
  }
  
  th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0,0,0,.87);
    border-top: none;
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const SortableHeader = styled.th<{ $active?: boolean; $direction?: string }>`
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: #f0f0f0 !important;
  }
  
  &::after {
    content: '${props => {
      if (!props.$active) return '';
      return props.$direction === 'asc' ? ' ▲' : ' ▼';
    }}';
    font-size: 10px;
  }
`;

const ViewLink = styled(Link)`
  color: #4183c4;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyMessage = styled.p`
  padding: 20px;
  color: #666;
  text-align: center;
`;

const ErrorMessage = styled.div`
  padding: 20px;
  color: #9f3a38;
  background: #fff6f6;
  border: 1px solid #e0b4b4;
  border-radius: 4px;
  text-align: center;
  margin: 20px 0;
`;

const Pagination = styled.nav`
  display: flex;
  gap: 4px;
  margin-top: 16px;
`;

const PageButton = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  padding: 8px 12px;
  border: 1px solid rgba(34,36,38,.15);
  background: ${props => props.$active ? '#2185d0' : '#fff'};
  color: ${props => props.$active ? '#fff' : 'rgba(0,0,0,.87)'};
  border-radius: 4px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.45 : 1};
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#1678c2' : '#f0f0f0'};
  }
`;

const LoadingMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #666;
`;

// ========================================
// Event Types - Matching legacy
// ========================================

const AVAILABLE_EVENT_TYPES = [
  { value: 'bulk_story_tagging', label: 'Bulk story tagging' },
  { value: 'bulk_story_reassignment', label: 'Bulk story reassignment' },
  { value: 'bulk_user_upload', label: 'Bulk User Upload from Admin2' },
  { value: 'cms_push_failure', label: 'CMS push failure' },
  { value: 'cms_push_success', label: 'CMS push success' },
  { value: 'cms_test_failure', label: 'CMS test failure' },
  { value: 'cms_test_success', label: 'CMS test success' },
  { value: 'document_updated', label: 'Document updates' },
  { value: 'feature_added_to_package', label: 'Feature added to package' },
  { value: 'feature_removed_from_package', label: 'Feature removed from package' },
  { value: 'integration_created', label: 'Integration created' },
  { value: 'integration_destroyed', label: 'Integration destroyed' },
  { value: 'manual_transaction_created', label: 'Manual transaction created' },
  { value: 'product_package_switched', label: 'Product package switched' },
  { value: 'property_created', label: 'Property created' },
  { value: 'property_updated', label: 'Property updated' },
  { value: 'sso_sign_in_failure', label: 'SSO sign in failure' },
  { value: 'sso_sign_in_success', label: 'SSO sign in success' },
  { value: 'story_created', label: 'Story created' },
  { value: 'story_import_failure', label: 'Story import failure' },
  { value: 'story_import_success', label: 'Story import success' },
];

const AVAILABLE_RESOURCE_TYPES = [
  { value: 'story', label: 'Story' },
  { value: 'user', label: 'User' },
  { value: 'pitch', label: 'Pitch' },
  { value: 'pitch_request', label: 'Pitch Request' },
  { value: 'publication', label: 'Publication' },
  { value: 'account', label: 'Account' },
];

// ========================================
// Helper Functions
// ========================================


const titleize = (str: string): string => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// ========================================
// Component
// ========================================

interface Actor {
  id: string;
  name: string;
}

function AccountAuditEventsTab() {
  const { account } = useAccountContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const { setTimezoneFromResponse, formatDateTime } = useUserTimezone();
  
  const [filters, setFilters] = useState<AuditEventFilters>(() => ({
    eventType: searchParams.get('event_type') || '',
    actor: searchParams.get('actor') || '',
    startDatetime: searchParams.get('start_datetime') || '',
    endDatetime: searchParams.get('end_datetime') || '',
    linkedResourceType: searchParams.get('linked_resource_type') || '',
    linkedResourceId: searchParams.get('linked_resource_id') || '',
    sortBy: searchParams.get('sort_by') || 'created_at',
    sortDirection: (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc',
    page: parseInt(searchParams.get('page') || '1', 10),
  }));
  
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [actors, setActors] = useState<Actor[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasFilters, setHasFilters] = useState(false);
  // Track if response was HTML (legacy audit service returns HTML)
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isHtmlResponse, setIsHtmlResponse] = useState(false);
  // Track error state to show user-friendly message instead of crashing
  const [error, setError] = useState<string | null>(null);

  // Helper to safely extract actors array from response
  const extractActors = (data: unknown): Actor[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object' && data !== null && 'actors' in data) {
      const actors = (data as { actors: unknown }).actors;
      return Array.isArray(actors) ? actors : [];
    }
    return [];
  };

  const fetchActors = useCallback(async () => {
    if (!account) return;
    
    try {
      const response = await api.get(`/admin2/accounts/${account.id}/audit_events/actors.json`);
      const actorsList = extractActors(response.data);
      setActors(actorsList);
    } catch (err) {
      // Fallback: actors might be included in the main response
      console.error('Failed to fetch actors:', err);
      // Don't set error here - actors are optional, we can still show events
    }
  }, [account]);

  const fetchEvents = useCallback(async (currentFilters: AuditEventFilters) => {
    if (!account) return;
    
    setLoading(true);
    setHtmlContent(null);
    setIsHtmlResponse(false);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (currentFilters.eventType) params.append('event_type', currentFilters.eventType);
      if (currentFilters.actor) params.append('actor', currentFilters.actor);
      if (currentFilters.startDatetime) params.append('start_datetime', currentFilters.startDatetime);
      if (currentFilters.endDatetime) params.append('end_datetime', currentFilters.endDatetime);
      if (currentFilters.linkedResourceType) params.append('linked_resource_type', currentFilters.linkedResourceType);
      if (currentFilters.linkedResourceId) params.append('linked_resource_id', currentFilters.linkedResourceId);
      params.append('sort_by', currentFilters.sortBy);
      params.append('sort_direction', currentFilters.sortDirection);
      params.append('page', currentFilters.page.toString());
      
      // First try JSON endpoint
      try {
        const response = await api.get(
          `/admin2/accounts/${account.id}/audit_events.json?${params.toString()}`
        );
        
        const data = response.data;
        
        // Check if we got HTML instead of JSON (service may return HTML)
        if (typeof data === 'string' && data.trim().startsWith('<')) {
          setIsHtmlResponse(true);
          setHtmlContent(data);
          setEvents([]);
          setPageCount(1);
        } else {
          // Safely extract events array
          const eventsList = Array.isArray(data.events) ? data.events : [];
          setEvents(eventsList);
          setPageCount(data.page_count || data.pageCount || 1);
          setTimezoneFromResponse(data);
          
          // Get actors from response if available (safely)
          if (actors.length === 0) {
            const actorsList = extractActors(data);
            if (actorsList.length > 0) {
              setActors(actorsList);
            }
          }
        }
      } catch (jsonError) {
        // If JSON fails, try fetching as HTML (legacy audit service behavior)
        console.warn('JSON request failed, trying HTML fallback:', jsonError);
        
        try {
          const htmlResponse = await api.get(
            `/admin2/accounts/${account.id}/audit_events?${params.toString()}`,
            { 
              responseType: 'text',
              headers: { 'Accept': 'text/html' }
            }
          );
          
          if (typeof htmlResponse.data === 'string') {
            setIsHtmlResponse(true);
            // Extract body content if it's a full HTML page
            const bodyMatch = htmlResponse.data.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
            const mainContent = htmlResponse.data.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
            const contentMatch = htmlResponse.data.match(/<div[^>]*class="[^"]*content[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
            
            setHtmlContent(
              mainContent?.[1] || 
              contentMatch?.[1] || 
              bodyMatch?.[1] || 
              htmlResponse.data
            );
            setEvents([]);
            setPageCount(1);
          }
        } catch (htmlError) {
          // Both JSON and HTML failed - show error state
          console.error('Both JSON and HTML requests failed:', htmlError);
          setError('Unable to load audit events. The audit event service may be temporarily unavailable.');
          setEvents([]);
          setPageCount(1);
        }
      }
      
      setHasFilters(
        !!(currentFilters.eventType || 
           currentFilters.actor ||
           currentFilters.startDatetime || 
           currentFilters.endDatetime || 
           currentFilters.linkedResourceType)
      );
    } catch (error) {
      console.error('Failed to load audit events:', error);
      setError('Failed to load audit events. Please try again later.');
      setEvents([]);
      setPageCount(1);
      setIsHtmlResponse(false);
      setHtmlContent(null);
    } finally {
      setLoading(false);
    }
  }, [account, actors.length]);

  useEffect(() => {
    if (account) {
      fetchActors();
      fetchEvents(filters);
    }
  }, [account]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newParams = new URLSearchParams();
    if (filters.eventType) newParams.set('event_type', filters.eventType);
    if (filters.actor) newParams.set('actor', filters.actor);
    if (filters.startDatetime) newParams.set('start_datetime', filters.startDatetime);
    if (filters.endDatetime) newParams.set('end_datetime', filters.endDatetime);
    if (filters.linkedResourceType) newParams.set('linked_resource_type', filters.linkedResourceType);
    if (filters.linkedResourceId) newParams.set('linked_resource_id', filters.linkedResourceId);
    newParams.set('sort_by', filters.sortBy);
    newParams.set('sort_direction', filters.sortDirection);
    newParams.set('page', '1');
    
    setSearchParams(newParams);
    
    const updatedFilters = { ...filters, page: 1 };
    setFilters(updatedFilters);
    fetchEvents(updatedFilters);
  };

  const handleFilterChange = (field: keyof AuditEventFilters, value: string | number) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    const resetFilters: AuditEventFilters = {
      eventType: '',
      actor: '',
      startDatetime: '',
      endDatetime: '',
      linkedResourceType: '',
      linkedResourceId: '',
      sortBy: 'created_at',
      sortDirection: 'desc',
      page: 1,
    };
    setFilters(resetFilters);
    setSearchParams(new URLSearchParams());
    fetchEvents(resetFilters);
  };

  const handleSort = (column: string) => {
    const newDirection = 
      filters.sortBy === column && filters.sortDirection === 'asc' 
        ? 'desc' 
        : 'asc';
    
    const newFilters: AuditEventFilters = {
      ...filters,
      sortBy: column,
      sortDirection: newDirection,
      page: 1,
    };
    
    setFilters(newFilters);
    fetchEvents(newFilters);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pageCount) return;
    
    const newFilters = { ...filters, page: newPage };
    setFilters(newFilters);
    fetchEvents(newFilters);
  };

  const handleDownloadCsv = () => {
    if (!account) return;
    
    const params = new URLSearchParams();
    if (filters.eventType) params.append('event_type', filters.eventType);
    if (filters.actor) params.append('actor', filters.actor);
    if (filters.startDatetime) params.append('start_datetime', filters.startDatetime);
    if (filters.endDatetime) params.append('end_datetime', filters.endDatetime);
    if (filters.linkedResourceType) params.append('linked_resource_type', filters.linkedResourceType);
    if (filters.linkedResourceId) params.append('linked_resource_id', filters.linkedResourceId);
    
    window.location.href = `/admin2/accounts/${account.id}/audit_events.csv?${params.toString()}`;
    toast.info('CSV export has been queued. You will receive an email when it is ready.');
  };

  const renderPagination = () => {
    if (pageCount <= 1) return null;
    
    const pages: number[] = [];
    for (let i = 1; i <= pageCount; i++) {
      pages.push(i);
    }
    
    return (
      <Pagination>
        <PageButton 
          $disabled={filters.page <= 2}
          onClick={() => handlePageChange(1)}
          disabled={filters.page <= 2}
        >
          ⏮
        </PageButton>
        
        <PageButton 
          $disabled={filters.page <= 1}
          onClick={() => handlePageChange(filters.page - 1)}
          disabled={filters.page <= 1}
        >
          ◀
        </PageButton>
        
        {pages.map(page => (
          <PageButton
            key={page}
            $active={page === filters.page}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </PageButton>
        ))}
        
        <PageButton 
          $disabled={filters.page >= pageCount}
          onClick={() => handlePageChange(filters.page + 1)}
          disabled={filters.page >= pageCount}
        >
          ▶
        </PageButton>
        
        <PageButton 
          $disabled={filters.page >= pageCount - 1 || filters.page >= pageCount}
          onClick={() => handlePageChange(pageCount)}
          disabled={filters.page >= pageCount - 1 || filters.page >= pageCount}
        >
          ⏭
        </PageButton>
      </Pagination>
    );
  };

  return (
    <div>
      <SectionTitle>Audit Events</SectionTitle>
      
      <FormContainer onSubmit={handleSubmit}>
        <FieldGroup $width="160px">
          <FieldLabel htmlFor="event_type">Event type</FieldLabel>
          <Select
            id="event_type"
            value={filters.eventType}
            onChange={(e) => handleFilterChange('eventType', e.target.value)}
          >
            <option value="">All event types</option>
            {AVAILABLE_EVENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </Select>
        </FieldGroup>
        
        <FieldGroup $width="200px">
          <FieldLabel htmlFor="actor">Actor</FieldLabel>
          <Select
            id="actor"
            value={filters.actor}
            onChange={(e) => handleFilterChange('actor', e.target.value)}
          >
            <option value="">All actors</option>
            <option value="system">*System*</option>
            {actors.map(actor => (
              <option key={actor.id} value={`user_${actor.id}`}>{actor.name}</option>
            ))}
          </Select>
        </FieldGroup>
        
        <FieldGroup $width="180px">
          <FieldLabel htmlFor="start_datetime">Start date/time</FieldLabel>
          <DateTimeInput
            id="start_datetime"
            type="datetime-local"
            value={filters.startDatetime}
            onChange={(e) => handleFilterChange('startDatetime', e.target.value)}
          />
        </FieldGroup>
        
        <FieldGroup $width="180px">
          <FieldLabel htmlFor="end_datetime">End date/time</FieldLabel>
          <DateTimeInput
            id="end_datetime"
            type="datetime-local"
            value={filters.endDatetime}
            onChange={(e) => handleFilterChange('endDatetime', e.target.value)}
          />
        </FieldGroup>
        
        <FieldGroup $width="140px">
          <FieldLabel htmlFor="linked_resource_type">Linked Resource</FieldLabel>
          <Select
            id="linked_resource_type"
            value={filters.linkedResourceType}
            onChange={(e) => handleFilterChange('linkedResourceType', e.target.value)}
          >
            <option value="">Select type</option>
            {AVAILABLE_RESOURCE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </Select>
        </FieldGroup>
        
        <FieldGroup $width="120px" $hidden={!filters.linkedResourceType}>
          <FieldLabel htmlFor="linked_resource_id">Resource ID</FieldLabel>
          <TextInput
            id="linked_resource_id"
            type="text"
            value={filters.linkedResourceId}
            onChange={(e) => handleFilterChange('linkedResourceId', e.target.value)}
          />
        </FieldGroup>
        
        <PrimaryButton type="submit" disabled={loading}>
          Apply filters
        </PrimaryButton>
        
        <SecondaryButton type="button" onClick={handleReset}>
          Reset
        </SecondaryButton>
      </FormContainer>
      
      <DownloadRow>
        <PrimaryButton type="button" onClick={handleDownloadCsv}>
          Download CSV
        </PrimaryButton>
      </DownloadRow>
      
      {loading ? (
        <LoadingMessage>Loading audit events...</LoadingMessage>
      ) : error ? (
        // Show error message instead of crashing
        <ErrorMessage>{error}</ErrorMessage>
      ) : isHtmlResponse && htmlContent ? (
        // Render HTML response from legacy audit service
        <HtmlContainer 
          ref={htmlContainerRef}
          dangerouslySetInnerHTML={{ __html: htmlContent }} 
        />
      ) : events.length === 0 ? (
        <EmptyMessage>
          {hasFilters 
            ? 'There are no audit events that match the current filters.'
            : 'There are no audit events for this account.'}
        </EmptyMessage>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th>Actor</th>
                <SortableHeader
                  $active={filters.sortBy === 'created_at'}
                  $direction={filters.sortDirection}
                  onClick={() => handleSort('created_at')}
                >
                  Created At
                </SortableHeader>
                <SortableHeader
                  $active={filters.sortBy === 'event_type'}
                  $direction={filters.sortDirection}
                  onClick={() => handleSort('event_type')}
                >
                  Event Type
                </SortableHeader>
                <th>Message</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id}>
                  <td>{event.actorName || event.actor}</td>
                  <td>{formatDateTime(event.createdAt)}</td>
                  <td>{titleize(event.eventType)}</td>
                  <td>{event.interpolatedMessage || event.message}</td>
                  <td>
                    {event.id && account && (
                      <ViewLink to={`/admin2/accounts/${account.id}/audit_events/${event.id}`}>
                        View
                      </ViewLink>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {renderPagination()}
        </>
      )}
    </div>
  );
}

export default AccountAuditEventsTab;
